import "./ConversionLatencyHistogram.scss";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import * as R from "ramda";
import AutoSizer from "react-virtualized-auto-sizer";
import {
  Dropdown,
  DropdownToggleType,
  DownloadDropdown,
  AxesSkeleton,
  Skeleton,
  BarGraphSkeleton,
} from "../../Components";
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
} from "../../Components/Charts/ChartConstants";
import {
  HistogramRow,
  GetHistogramParams,
  PerformancePreset as Preset,
  Prefix as StreamingPrefix,
} from "@blisspointmedia/bpm-types/dist/StreamingPerformance";
import { awaitJSON, StreamingPerformanceLambdaFetch } from "../../utils/fetch-utils";
import { useSetError } from "../../redux/modals";
import { useIsMounted } from "../../utils/hooks/useDOMHelpers";
import { BarChart, YAxis, Bar, Tooltip, XAxis, CartesianGrid } from "recharts";
import { Brand60 } from "../../utils/colors";
import ChartContainer from "../../Components/ChartContainer";
import { formatPercent } from "../../utils/format-utils";
import ChartTooltipV2 from "../../Components/Charts/ChartTooltip/ChartTooltipV2";
import { downloadPNG, exportToExcel } from "../../utils/download-utils";
import { Histogram } from "./Histogram";

export const VIEW_TYPE_OPTIONS = [
  { value: "Discrete", label: "Discrete" },
  { value: "Cumulative", label: "Cumulative" },
];

interface ConversionLatencyHistogramProps {
  presetID?: number;
  kpi: string;
  start: string;
  end: string;
  prefix: StreamingPrefix;
  preset?: Preset;
  oldVersion?: boolean;
}

const ConversionLatencyHistogram = ({
  presetID,
  kpi,
  start,
  end,
  prefix,
  preset,
  oldVersion = false,
}: ConversionLatencyHistogramProps): JSX.Element => {
  const setError = useSetError();
  const getIsMounted = useIsMounted();

  const [histDataMap, setHistDataMap] = useState<Record<string, HistogramRow[] | undefined>>({});
  const [viewType, setViewType] = useState("Discrete");

  const histKey = useMemo(() => `${presetID}_${kpi}_${start}_${end}_${preset?.impsBetween}`, [
    end,
    kpi,
    preset?.impsBetween,
    presetID,
    start,
  ]);

  useEffect(() => {
    if (!histDataMap[histKey]) {
      (async () => {
        try {
          let res = await StreamingPerformanceLambdaFetch<GetHistogramParams>("/histogram", {
            params: {
              kpi,
              start,
              end,
              prefix,
              impsBetween: preset?.impsBetween || true,
            },
          });
          let rows = await awaitJSON<HistogramRow[]>(res);
          if (getIsMounted()) {
            setHistDataMap(map => ({ ...map, [histKey]: rows }));
          }
        } catch (e) {
          setError({
            message: e instanceof Error ? e.message : String(e),
            reportError: e,
          });
        }
      })();
    }
  }, [preset, histDataMap, histKey, start, end, setError, kpi, getIsMounted, prefix]);

  const histData = histDataMap[histKey];

  const excelDownload = useCallback(() => {
    exportToExcel(histData || [], "Streaming Conversion Latency");
  }, [histData]);

  const pngDownload = useCallback(async () => {
    await downloadPNG(".conversionLatencyContainer", "Streaming Conversion Latency");
  }, []);

  return oldVersion ? (
    <Histogram data={histData} />
  ) : (
    <ChartContainer
      enableHoverDesign
      title={
        <>
          <Dropdown
            type={DropdownToggleType.WIDGET_TITLE}
            value={viewType}
            options={VIEW_TYPE_OPTIONS}
            onChange={option => setViewType(option)}
          />
          <div>Conversion Latency</div>
        </>
      }
      rightActions={
        <DownloadDropdown
          size="sm"
          onClickOptions={[excelDownload, pngDownload]}
          disabled={!histData}
        />
      }
    >
      {
        <div className="conversionLatencyChart">
          {histData ? (
            histData.length ? (
              <AutoSizer>
                {({ width, height }) => (
                  <BarChart
                    data={histData}
                    width={width}
                    height={height}
                    margin={{ top: 10, right: 20, bottom: 10, left: 10 }}
                  >
                    <CartesianGrid
                      vertical={false}
                      stroke={CARTESIAN_GRID_STROKE}
                      strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                    />
                    <Bar dataKey={viewType === "Discrete" ? "pct" : "cumulative"} fill={Brand60} />
                    <XAxis
                      dataKey="day"
                      tickFormatter={day => `Day ${day}`}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      width={30}
                      dataKey={viewType === "Discrete" ? "pct" : "cumulative"}
                      tickFormatter={val => formatPercent(val as number, 0)}
                      interval={0}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      content={({ label, payload }) => {
                        const items =
                          payload?.map(item => {
                            let { name, value } = item;
                            name = R.defaultTo("", name);
                            value = R.defaultTo(0, value as number);
                            return {
                              label: name === "pct" ? "Percent" : "Cumulative",
                              value: formatPercent(value as number, 2),
                              color: Brand60,
                            };
                          }) || [];

                        const formattedLabel = `${label} day${label === 1 ? "" : "s"} later`;
                        return (
                          <ChartTooltipV2
                            headers={[formattedLabel]}
                            items={items}
                            itemKeyShape="square"
                          />
                        );
                      }}
                      cursor={{ opacity: 0.4 }}
                    />
                  </BarChart>
                )}
              </AutoSizer>
            ) : (
              <div className="noData">No conversion latency data.</div>
            )
          ) : (
            <Skeleton>
              <BarGraphSkeleton barWidth={14} gutter={3} verticalPadding={5} />
              <AxesSkeleton size={3} />
            </Skeleton>
          )}
        </div>
      }
    </ChartContainer>
  );
};

export default ConversionLatencyHistogram;
